// Area-unit metadata. `perAcre` converts the canonical area-in-acres to each
// unit. The boundary delineation toggle only surfaces acres/hectares (others
// remain here for the abbr/conversion lookup). The buffer step has its own
// distance ft/m toggle.
export const AREA_UNITS = [
    { key: 'acres', label: 'Acres', abbr: 'ac', perAcre: 1 },
    { key: 'hectares', label: 'Hectares', abbr: 'ha', perAcre: 0.404685642 },
    { key: 'sqm', label: 'Square meters', abbr: 'm²', perAcre: 4046.8564224 },
    { key: 'sqkm', label: 'Square kilometers', abbr: 'km²', perAcre: 0.0040468564 },
    { key: 'sqft', label: 'Square feet', abbr: 'ft²', perAcre: 43560 },
    { key: 'sqmi', label: 'Square miles', abbr: 'mi²', perAcre: 0.0015625 },
];
const HECTARES_PER_ACRE = 0.404685642;
// Magnitude-aware area formatting: more decimals for small areas, comma-grouped
// integers for large ones.
export const formatAreaValue = (areaValue) => {
    if (areaValue < 0.01)
        return `< 0.01`;
    if (areaValue < 1)
        return `${areaValue.toFixed(3)}`;
    if (areaValue < 10)
        return `${areaValue.toFixed(2)}`;
    if (areaValue < 100)
        return `${areaValue.toFixed(1)}`;
    return `${Math.round(areaValue).toLocaleString()}`;
};
// Single source of truth for the delineation area readout. The server zeroes
// the area when the polygon is out of bounds and embeds the threshold only in
// its message text, so out-of-bounds states show the parsed threshold rather
// than a bogus value.
export function computeAreaReadout(pointCount, areaInAcres, areaUnit, areaData) {
    var _a, _b;
    const activeUnit = (_a = AREA_UNITS.find((u) => u.key === areaUnit)) !== null && _a !== void 0 ? _a : AREA_UNITS[0];
    const failMsg = areaData && areaData.success === false ? (areaData.message || '') : '';
    const lowered = failMsg.toLowerCase();
    const isMaxExceeded = lowered.includes('maximum area');
    const isMinNotMet = lowered.includes('minimum area');
    const match = (_b = areaData === null || areaData === void 0 ? void 0 : areaData.message) === null || _b === void 0 ? void 0 : _b.match(/([\d,]+(?:\.\d+)?)\s*acre/i);
    const thresholdAcres = match ? parseFloat(match[1].replace(/,/g, '')) : null;
    const thresholdBothUnits = thresholdAcres == null
        ? null
        : `${formatAreaValue(thresholdAcres)} ac (${formatAreaValue(thresholdAcres * HECTARES_PER_ACRE)} ha)`;
    const polygonArea = areaInAcres * activeUnit.perAcre;
    let readoutValue;
    if (pointCount < 3) {
        readoutValue = '—';
    }
    else if (isMaxExceeded) {
        readoutValue = thresholdAcres != null ? `> ${formatAreaValue(thresholdAcres * activeUnit.perAcre)}` : '> Max';
    }
    else if (isMinNotMet) {
        readoutValue = thresholdAcres != null ? `< ${formatAreaValue(thresholdAcres * activeUnit.perAcre)}` : '< Min';
    }
    else {
        readoutValue = formatAreaValue(polygonArea);
    }
    return { abbr: activeUnit.abbr, isMaxExceeded, isMinNotMet, thresholdAcres, thresholdBothUnits, readoutValue };
}
